import React, { useMemo } from "react";
import "./InfoModal.css";
import { useTranslation } from "react-i18next";

/**
 * Reusable Bootstrap confirmation modal.
 *
 * Props:
 * - modalId: string (required)
 * - title: string (required)
 * - body: string | ReactNode (required)
 * - icon: string (optional, emoji)
 * - confirmText: string (optional)
 * - cancelText: string (optional)
 * - confirmVariantClassName: string (optional, bootstrap btn class; default "btn-danger")
 * - onConfirm: () => void|Promise<void> (required)
 * - confirmDisabled: boolean (optional)
 * - cancelDisabled: boolean (optional)
 */
export default function ConfirmModal({
  modalId,
  title,
  body,
  icon = "✅",
  confirmText,
  cancelText,
  confirmVariantClassName = "btn-danger",
  onConfirm,
  confirmDisabled = false,
  cancelDisabled = false,
}) {
  const { t } = useTranslation();

  const confirmLabel = useMemo(
    () => confirmText || t("general.confirm") || "Confirm",
    [confirmText, t]
  );
  const cancelLabel = useMemo(
    () => cancelText || t("general.cancel") || "Cancel",
    [cancelText, t]
  );

  return (
    <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-theme as-info-modal">
          <div className="modal-header">
            <div className="as-info-modal-header">
              <div className="as-info-modal-title">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  {icon}
                </span>
                <h5 className="modal-title">{title}</h5>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={t("general.close") || "Close"}
                disabled={cancelDisabled}
              />
            </div>
          </div>
          <div className="modal-body">
            {typeof body === "string" ? <p className="mb-0">{body}</p> : body}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-sm as-btn-soft"
              data-bs-dismiss="modal"
              disabled={cancelDisabled}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${confirmVariantClassName}`}
              onClick={onConfirm}
              disabled={confirmDisabled}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

